import { createAction, createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { categoriesApi } from "@api";
import { RootState, setSelectedLocations } from ".";
import {
    CategoryData,
    CategoryTours,
    HomePageProductState,
    HomePageSchema,
    ITour,
} from "@interfaces";
import {
    filter,
    find,
    forEach,
    isArray,
    isEmpty,
    isNil,
    map,
    reduce,
    toLower,
} from "lodash";

export const setCurrentCacheKey = createAction<string>("homePageProduct/setCurrentCacheKey");

const initialState: HomePageProductState = {
    homePageProducts: {},
    cache: {},
    homePageSchema: null,
    selectedChipType: "",
    status: "idle",
    error: null,
    currentCacheKey: null,
};

// Helper functions
const generateCacheKey = (
    chipType: string,
    locations: {
        country?: { id: string } | null | undefined;
        state?: { id: string } | null | undefined;
        region?: { id: string } | null | undefined;
        locality?: { id: string } | null | undefined;
    },
) => {
    const { country, state, region, locality } = locations;
    return `${chipType}_${country?.id ?? ""}_${state?.id ?? ""}_${region?.id ?? ""}_${locality?.id ?? ""}`;
};


const transformData = (filteredResponses: any) => {
    const transformedData: Record<string, CategoryTours> = {};

    forEach(filteredResponses, ({ label, data, labelId }: any) => {
        if (!isArray(data) || isEmpty(data)) {
            console.warn(`[transformData] Skipping label ${label} - no valid data`);
            return;
        }

        const tours: ITour[] = data
            .filter((tour: any) => {
                if (!tour || !tour.productId) {
                    console.warn(`[transformData] Skipping invalid tour:`, tour);
                    return false;
                }
                return true;
            })
            .map((tour: any): ITour => ({
                productId: tour.productId,
                name: tour.name,
                location:
                    tour.categories?.startLocationName && tour.categories?.countryNames?.[0]
                        ? `${tour.categories.startLocationName}, ${tour.categories.countryNames[0]}`
                        : tour.location || "Unknown Location",
                categories: tour.categories,
                durationDays: tour.durationDays,
                durationNight: tour.durationNight,
                rrp: Number(tour.rrp || 0),
                productImagePath: tour.productLargeSizeImagePath,
                productSmallToMediumSizeImagePath: tour.productSmallToMediumSizeImagePath,
                productLargeSizeImagePath: tour.productLargeSizeImagePath,
                faresprices: tour.faresprices,
                description: tour.description,
                supplier: tour.supplier || tour.supplierName,
                productRanking: tour.productRanking,
                productClass: tour.productClass,
            }))
            .sort((a, b) => {
                if (isNil(a.productRanking) && isNil(b.productRanking)) return 0;
                if (isNil(a.productRanking)) return 1;
                if (isNil(b.productRanking)) return -1;
                return (a.productRanking ?? 0) - (b.productRanking ?? 0);
            });

        if (!isEmpty(tours)) {
            transformedData[label] = {
                categoryName: label,
                labelId,
                tours,
            };
        }
    });

    return transformedData;
};


export const fetchHomePageSchema = createAsyncThunk<HomePageSchema, void, { rejectValue: string }>(
    "HomePageSchema/fetchHomePageSchema",
    async (_, thunkAPI) => {
        try {
            const res = await categoriesApi.getSchema();
            return res.data;
        } catch (error) {
            console.error(error);
            return thunkAPI.rejectWithValue("Failed to fetch categories");
        }
    },
);

export const fetchHomePageProductWithType = createAsyncThunk<
    { data: Record<string, CategoryTours>; cacheKey: string; usedCache: boolean },
    void,
    { rejectValue: string; state: RootState }
>("HomePageProductWithType/fetchHomePageProductWithType", async (_, thunkAPI) => {
    try {
        const state = thunkAPI.getState();
        const schema = state.homePageProduct.homePageSchema;
        const chipType = toLower(state.homePageProduct.selectedChipType).replace(/s$/, "");
        const selectedLocations = state.locationSearch?.selectedLocations ?? {};

        const cacheKey = generateCacheKey(chipType, selectedLocations);

        // Clear cache if location differs from current cache key
        if (state.homePageProduct.currentCacheKey) {
            const currentLocationPart = state.homePageProduct.currentCacheKey
                .split("_")
                .slice(1)
                .join("_");
            const newLocationPart = cacheKey.split("_").slice(1).join("_");
            if (currentLocationPart !== newLocationPart) {
                thunkAPI.dispatch({ type: "homePageProduct/clearCache" });
            }
        }

        thunkAPI.dispatch(setCurrentCacheKey(cacheKey));

        const cache = state.homePageProduct.cache;
        if (cache && cache[cacheKey]) {
            // Show cached data immediately
            thunkAPI.dispatch({
                type: "homePageProduct/showCachedData",
                payload: { data: cache[cacheKey], cacheKey },
            });
            // Continue to fetch fresh data
        }

        const selectedSection = find(
            schema?.data,
            (section: any) => toLower(section.type).replace(/s$/, "") === chipType,
        );

        if (!selectedSection) {
            return { data: {}, cacheKey, usedCache: false };
        }

        let rawResponses: { label: string; data: any[]; labelId?: number }[] = [];

        const res = await categoriesApi.getHomepageProduct({
            chipType,
            selectedLocations,
        });
        const data = (res.data?.data ?? []) as CategoryData[];
        rawResponses = map(data, (item) => {
            const products = map(item.products, (p: any) => p.json || p);

            return {
                label: item.label,
                labelId: item.label_id,
                data: products,
            };
        });

        // FIX: Filter out empty responses before transformation
        const filteredResponses = filter(
            rawResponses,
            ({ data }: any) => isArray(data) && !isEmpty(data),
        );

        const transformedData = transformData(filteredResponses);

        return { data: transformedData, cacheKey, usedCache: false };
    } catch (err) {
        console.error("Fetch HomePage failed:", err);
        return thunkAPI.rejectWithValue("Failed to fetch products");
    }
});

const homePageProductSlice = createSlice({
    name: "homePageProduct",
    initialState,
    reducers: {
        setChipType: (state, action: PayloadAction<string>) => {
            if (state.selectedChipType === action.payload) return;
            state.selectedChipType = action.payload;
            // FIX: Don't clear products immediately, let the fetch handle it
            state.status = "idle";
        },
        setCurrentCacheKey: (state, action: PayloadAction<string>) => {
            state.currentCacheKey = action.payload;
        },
        showCachedData: (
            state,
            action: PayloadAction<{ data: Record<string, CategoryTours>; cacheKey: string }>,
        ) => {
            const { data, cacheKey } = action.payload;
            if (cacheKey === state.currentCacheKey) {
                state.homePageProducts = data;
                state.status = "succeeded";
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(setSelectedLocations, (state) => {
                state.cache = {};
                state.currentCacheKey = null;
                state.homePageProducts = {}; // Clear products when location changes
            })
            .addCase(fetchHomePageSchema.pending, (state) => {
                state.status = "loading";
            })
            .addCase(
                fetchHomePageSchema.fulfilled,
                (state, action: PayloadAction<HomePageSchema>) => {
                    state.status = "succeeded";
                    state.homePageSchema = action.payload;
                },
            )
            .addCase(fetchHomePageSchema.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload ?? "An unknown error occurred";
            })
            .addCase(fetchHomePageProductWithType.pending, (state) => {
                state.status = "loading";
                state.homePageProducts = {};
            })
            .addCase(fetchHomePageProductWithType.fulfilled, (state, action) => {
                const { data, cacheKey, usedCache } = action.payload;

                if (cacheKey === state.currentCacheKey) {
                    if (!usedCache) {
                        state.cache[cacheKey] = data;
                    }
                    state.homePageProducts = data;
                    state.status = "succeeded";
                    state.error = null;
                }
            })
            .addCase(fetchHomePageProductWithType.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload ?? "Failed to fetch products";
            });
    },
});
// Helper function to extract category data from fetched products for destinations
export const extractCategoryDataFromProducts = (
    homePageProducts: Record<string, CategoryTours>,
    labelName: string,
) => {
    const category = homePageProducts[labelName];
    if (!category?.tours || isEmpty(category.tours)) {
        return null;
    }

    // Extract categories from all tours in this label
    const categoriesMap = new Map<
        string,
        { category_id: string; category: string; category_type: string }
    >();

    forEach(category.tours, (tour: any) => {
        if (tour.categories) {
            // Extract activities
            if (!isEmpty(tour.categories.activities) && !isEmpty(tour.categories.activityNames)) {
                forEach(tour.categories.activities, (id: string, index: number) => {
                    const name = tour.categories.activityNames[index] || "Unknown Activity";
                    categoriesMap.set(id, {
                        category_id: id,
                        category: name,
                        category_type: "activities",
                    });
                });
            }

            // Extract accommodation
            if (
                !isEmpty(tour.categories.accommodation) &&
                !isEmpty(tour.categories.accommodationNames)
            ) {
                forEach(tour.categories.accommodation, (id: string, index: number) => {
                    const name =
                        tour.categories.accommodationNames[index] || "Unknown Accommodation";
                    categoriesMap.set(id, {
                        category_id: id,
                        category: name,
                        category_type: "accommodation",
                    });
                });
            }

            // Extract transport
            if (!isEmpty(tour.categories.transport) && !isEmpty(tour.categories.transportNames)) {
                forEach(tour.categories.transport, (id: string, index: number) => {
                    const name = tour.categories.transportNames[index] || "Unknown Transport";
                    categoriesMap.set(id, {
                        category_id: id,
                        category: name,
                        category_type: "transport",
                    });
                });
            }
        }
    });

    const categories = Array.from(categoriesMap.values());

    return {
        label: labelName,
        label_id: category.labelId,
        categories,
        categoryIds: map(categories, "category_id"),
        categoryMap: reduce(
            categories,
            (acc, cat) => {
                acc[cat.category_id] = cat.category;
                return acc;
            },
            {} as Record<string, string>,
        ),
        categoryType: categories[0]?.category_type || "activities",
    };
};

export const { setChipType } = homePageProductSlice.actions;
export const selectHomePageProduct = (state: RootState) => state.homePageProduct;
export default homePageProductSlice.reducer;
